import { Box, Container, Grid, Group, Paper, Progress, Stack, Text, Title, UnstyledButton } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"

import { queries } from "@/lib/queries"

export const Route = createFileRoute("/six-alphas")({
  component: SixAlphas,
})

const ALPHA_COLORS: Record<string, string> = {
  fee: "var(--pitch-alpha-fee)",
  sourcing: "var(--pitch-alpha-sourcing)",
  operating: "var(--pitch-alpha-operating)",
  structural: "var(--pitch-alpha-structural)",
  tax: "var(--pitch-alpha-tax)",
  exit: "var(--pitch-alpha-exit)",
}

function SixAlphas() {
  const { data, isPending, isError } = useQuery(queries.alphas())
  const [expandedAlpha, setExpandedAlpha] = useState<number | null>(null)

  if (isPending) {
    return (
      <Container size="xl">
        <Text c="var(--pitch-text-tertiary)">Loading alpha model…</Text>
      </Container>
    )
  }

  if (isError || !data) {
    return (
      <Container size="xl">
        <Text c="var(--pitch-negative)">Failed to load alpha model</Text>
      </Container>
    )
  }

  const { alphas, summary } = data
  const totalValue = Number(summary.totalIncrementalValueM)

  return (
    <Container size="xl">
      <Stack gap="xl">
        <Paper p="xl" radius={0} style={{ background: "var(--pitch-navy)" }}>
          <Stack gap="md" align="center">
            <Text
              size="sm"
              fw={500}
              c="var(--pitch-on-navy-muted)"
              style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              Six Alphas — Optimizing Value Capture
            </Text>
            <Group align="baseline" gap="xs">
              <Title order={1} c="white" ff="'Playfair Display', Georgia, serif" fw={400} style={{ fontSize: 56 }}>
                ${(totalValue / 1000).toFixed(2)}B
              </Title>
              <Text size="lg" c="var(--pitch-on-navy-muted)">
                incremental value
              </Text>
            </Group>
            <Text c="var(--pitch-on-navy)" ta="center" maw={600}>
              Same biology. Same capital deployed. ${summary.committedCapitalM}M committed.
            </Text>

            <Group gap="xl" mt="md">
              {[
                { label: "Hub-and-Spoke", moic: summary.hubMoic, color: "rgba(255,255,255,0.4)" },
                { label: "Traditional VC", moic: summary.vcMoic, color: "rgba(255,255,255,0.6)" },
                { label: "Civala", moic: summary.civilaMoic, color: "var(--pitch-alpha-tax)" },
              ].map(({ label, moic, color }) => (
                <Stack key={label} align="center" gap={4}>
                  <Text
                    ff="'Playfair Display', Georgia, serif"
                    style={{ fontSize: moic === summary.civilaMoic ? 40 : 28, fontWeight: 400, color }}
                  >
                    {moic}×
                  </Text>
                  <Text size="xs" c="var(--pitch-on-navy-muted)">
                    {label}
                  </Text>
                </Stack>
              ))}
            </Group>
          </Stack>
        </Paper>

        <Stack gap="md">
          {alphas.map((alpha) => {
            const isExpanded = expandedAlpha === alpha.number
            const color = ALPHA_COLORS[alpha.slug] ?? "var(--pitch-accent)"
            const valueM = Number(alpha.incrementalValueM)
            const pct = (valueM / totalValue) * 100

            return (
              <UnstyledButton
                key={alpha.number}
                onClick={() => setExpandedAlpha(isExpanded ? null : alpha.number)}
                style={{ width: "100%" }}
              >
                <Paper withBorder p="lg" radius={0} style={{ borderLeft: `4px solid ${color}` }}>
                  <Stack gap="md">
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="md" wrap="nowrap">
                        <Text
                          ff="'Playfair Display', Georgia, serif"
                          fw={400}
                          size="lg"
                          style={{ color, minWidth: 32 }}
                        >
                          {String(alpha.number).padStart(2, "0")}
                        </Text>
                        <Stack gap={2}>
                          <Text fw={600} size="md">
                            {alpha.name}
                          </Text>
                          <Text size="sm" c="var(--pitch-text-tertiary)">
                            {alpha.tagline}
                          </Text>
                        </Stack>
                      </Group>
                      <Box px={12} py={4} ta="center" style={{ minWidth: 90, border: `1px solid ${color}`, color }}>
                        <Text fw={600} size="md" c={color} ff="'Playfair Display', Georgia, serif">
                          ${valueM}M
                        </Text>
                      </Box>
                    </Group>

                    <Progress value={pct} color={color} size="xs" radius={0} />

                    {isExpanded && (
                      <Grid mt="sm" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Paper p="md" radius={0} bg="var(--pitch-positive-bg)">
                            <Text
                              size="xs"
                              fw={600}
                              c="var(--pitch-positive)"
                              mb="xs"
                              style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}
                            >
                              Civala
                            </Text>
                            <Text size="sm">{alpha.civalaApproach}</Text>
                          </Paper>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Paper p="md" radius={0} bg="var(--pitch-negative-bg)">
                            <Text
                              size="xs"
                              fw={600}
                              c="var(--pitch-negative)"
                              mb="xs"
                              style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}
                            >
                              Traditional
                            </Text>
                            <Text size="sm">{alpha.traditionalApproach}</Text>
                          </Paper>
                        </Grid.Col>
                        {alpha.keyMetric.fold(
                          () => null,
                          (keyMetric) => (
                            <Grid.Col span={12}>
                              <Paper p="sm" radius={0} bg="var(--pitch-inflection-bg)" ta="center">
                                <Text size="sm" fw={600} c="var(--pitch-inflection)">
                                  Key metric: {keyMetric}
                                </Text>
                              </Paper>
                            </Grid.Col>
                          ),
                        )}
                      </Grid>
                    )}
                  </Stack>
                </Paper>
              </UnstyledButton>
            )
          })}
        </Stack>

        <Paper withBorder p="lg" radius={0} bg="var(--pitch-surface-alt)">
          <Group justify="space-between">
            <Text fw={600} size="lg">
              Total Six Alphas
            </Text>
            <Text fw={400} size="xl" c="var(--pitch-alpha-tax)" ff="'Playfair Display', Georgia, serif">
              ${(totalValue / 1000).toFixed(2)}B
            </Text>
          </Group>
          <Text size="xs" c="var(--pitch-text-tertiary)" mt="xs">
            Source: Civala Alpha Compounding Model v3.0, April 2026. ${summary.committedCapitalM}M committed capital,
            base case.
          </Text>
        </Paper>
      </Stack>
    </Container>
  )
}
